/**
 * Shared failure handling for the operations that rewrite a thread — revert,
 * fork, and the sidebar's Duplicate (a fork at the last user message).
 *
 * All three are refused by the server while a turn holds the thread, and all
 * three used to swallow that refusal into `console.warn`.  That was the whole of
 * the "fork does nothing" report: a turn whose browser had gone away without the
 * explicit stop landing kept running server-side for as long as the request may
 * live, so the chat looked idle while every attempt came back 409, and nothing on
 * screen ever said so.
 *
 * A turn that is genuinely mid-flight and one that outlived its client are
 * indistinguishable from the outside, so this does not guess: it reports the
 * turn's age and offers to stop it, which is the same decision the Stop button
 * makes, taken by the only party who knows whether that work is still wanted.
 */
import { api, ApiRequestError } from '@/services/api';
import { toast } from '@/components/ui/Toast';

/** The server refused because a turn is still running on the thread. */
const TURN_RUNNING = 'STREAM_ACTIVE';
/** The server refused because a stopped turn is still saving what it did. */
const TURN_STOPPING = 'STREAM_STOPPING';

function refusalCode(err: unknown): string | null {
  return err instanceof ApiRequestError ? err.code : null;
}

/**
 * Waiting out a stopped turn is the machine's job, not the user's.
 *
 * A turn releases the thread once its final write lands — about a second, now
 * that a Stop actually kills the command it was running instead of leaving it
 * to run out its budget.  Telling someone to "try again in a moment" for that
 * second was asking them to be the retry loop, and when the moment was longer
 * than they expected the button just kept saying the same thing.
 *
 * Bounded, because a wait that never ends is its own bug: the server already
 * holds each attempt for up to ten seconds waiting for the turn to settle, so
 * three of them is a silent half-minute — past that the refusal is real and is
 * shown.
 */
const STOPPING_ATTEMPTS = 3;
const STOPPING_RETRY_MS = 1200;
/** Attempts already spent waiting out this thread's stopping turn. */
const stoppingWaits = new Map<string, { spent: number; at: number }>();
/** A fresh attempt after this long is a new request, not the same one retrying. */
const STOPPING_WINDOW_MS = 60_000;

function spendStoppingWait(threadId: string): number {
  const prev = stoppingWaits.get(threadId);
  const spent = prev && Date.now() - prev.at < STOPPING_WINDOW_MS ? prev.spent + 1 : 1;
  stoppingWaits.set(threadId, { spent, at: Date.now() });
  return spent;
}

/**
 * Report a failed thread-surgery attempt.
 *
 * A turn the user already stopped is waited out here rather than reported — it
 * is a second of the server finishing its write, and nothing about it needs a
 * person.  A turn still running is theirs to end, so that one is a toast
 * carrying the server's phrasing (including how long it has been running) and
 * the one action that lifts the block.  Everything else reports `fallback`, so
 * a real failure is never silent.
 */
export function reportSurgeryFailure(
  err: unknown,
  opts: {
    threadId: string;
    fallback: string;
    /**
     * Repeat the attempt once the thread is free.  Omitted where repeating it
     * alone would not finish the job — an edit's cut is followed by sending the
     * message, so there the block is only lifted and the user sends again.
     */
    retry?: () => void;
  },
): void {
  const code = refusalCode(err);
  const message = err instanceof ApiRequestError ? err.message : opts.fallback;
  const retry = opts.retry;
  if (code !== TURN_STOPPING) stoppingWaits.delete(opts.threadId);

  if (code === TURN_RUNNING) {
    toast.action(message, {
      label: retry ? 'Stop that turn and try again' : 'Stop that turn',
      onClick: () => {
        void api
          .post('/chat/abort', { threadId: opts.threadId })
          .then(() => retry?.())
          .catch(() => toast.error("Couldn't stop the running turn — try again in a moment."));
      },
    });
    return;
  }

  if (code === TURN_STOPPING) {
    if (retry && spendStoppingWait(opts.threadId) < STOPPING_ATTEMPTS) {
      window.setTimeout(retry, STOPPING_RETRY_MS);
      return;
    }
    stoppingWaits.delete(opts.threadId);
    if (retry) toast.action(message, { label: 'Try again', onClick: retry });
    else toast.error(message);
    return;
  }

  console.warn('[ai-chat] thread surgery failed:', err);
  toast.error(opts.fallback);
}
